import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { setUserSession } from "../../utils/storage";
import Footer from "../Layout/Footer";
import { toast } from "react-toastify";
import Button from "../../components/Button";
import {
  EyeIcon,
  EyeSlashIcon,
  UserIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import { axiosInstance } from "../../utils/axios";

const Login = () => {
  console.log("Login component rendered"); // Add this
  const [showPassword, setShowPassword] = React.useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  console.log("API Endpoint:", window.REACT_APP_API_ENDPOINT + "/login");

  const loginUser = useMutation({
    mutationFn: (data) =>
      axiosInstance.post(window.REACT_APP_API_ENDPOINT + "/login", data),
    onSuccess: (data) => {
      console.log("data",data);
      setUserSession(data.data.token, data.data.user);
      window.location.assign("/admin/devices");
    },
    onError: (error) => {
      toast.error("Invalid username or password. Please try again.");
      console.error("Login error:", error);
    },
  });

  const onSubmit = (formData) => {
    loginUser.mutate(formData);
  };

  return (
    <>
      <div className="flex min-h-screen bg-bgBlue flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row mt-10 sm:mx-auto sm:w-full sm:max-w-[1200px] mb-20">
          <div className="w-full lg:w-1/2 relative flex flex-col justify-center items-center px-6 py-12 shadow sm:rounded-lg sm:px-12 bg-gradient-to-br from-blue-800 via-bluePrimary to-blue-400 mr-4 mb-4 lg:mb-0 overflow-hidden">
            {/* Optional: Logo or Icon */}
            {/* <img src={logo} alt="Logo" className="w-20 h-20 mb-6" /> */}
            <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none select-none">
              {/* Decorative SVG or pattern */}
              <svg width="100%" height="100%">
                <circle cx="80%" cy="20%" r="120" fill="#fff" />
              </svg>
            </div>
            <div className="relative z-10 flex flex-col items-center w-full">
              <div className="w-16 h-1 bg-white rounded-full mb-6" />
              <h2 className="text-white text-4xl font-extrabold mb-6 drop-shadow-lg tracking-tight text-center">
                RitexApp
              </h2>
              <h3 className="text-white text-5xl font-extrabold mb-4 text-center leading-tight drop-shadow-lg">
                Start your journey with us
              </h3>
              <p className="text-blue-100 text-lg font-medium text-center mb-2">
                Your trusted companion for print ordering...
              </p>
              <span className="inline-block mt-4 px-4 py-2 bg-white/20 text-white rounded-full font-semibold text-sm tracking-wide shadow">
                Fast • Reliable • Secure
              </span>
            </div>
          </div>
          <div className="w-full lg:w-1/2 bg-white px-6 py-12 shadow sm:rounded-lg sm:px-12">
            <h1 className="font-bold text-4xl mb-4">Log In</h1>
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-4">
                <div className="mt-2 relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">
                    <UserIcon className="h-5 w-5" />
                  </span>
                  <input
                    id="username"
                    {...register("username", { required: true })}
                    type="text"
                    autoComplete="username"
                    placeholder="Username"
                    className="pl-10 block w-full rounded-md border-0 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    style={{
                      border: errors.username
                        ? "1px solid red"
                        : "2px solid transparent",
                    }}
                  />
                  {errors.username && (
                    <span className="text-xs text-red-600 absolute -bottom-5">
                      Username is required
                    </span>
                  )}
                </div>
              </div>
              <div className="mb-4">
                <div className="mt-2 relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">
                    <LockClosedIcon className="h-5 w-5" />
                  </span>
                  <input
                    id="password"
                    {...register("password", { required: true })}
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="Password"
                    className="pl-10 pr-10 block w-full rounded-md border-0 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    style={{
                      border: errors.password
                        ? "1px solid red"
                        : "2px solid transparent",
                    }}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 "
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                  {errors.password && (
                    <span className="text-xs text-red-600 absolute -bottom-5">
                      Password is required
                    </span>
                  )}
                </div>
              </div>
              {loginUser.isError && (
                <p className="mt-2 text-sm text-red-600">
                  Invalid username or password
                </p>
              )}
              <div className="flex items-center justify-between">
                <div className="text-sm leading-6">
                  <a
                    href="#"
                    className="font-semibold text-indigo-600 hover:text-indigo-500"
                  >
                    Forgot password?
                  </a>
                </div>
              </div>
              <div>
                <Button
                  type="submit"
                  className="uppercase flex w-full justify-center rounded-md bg-bluePrimary px-3 py-3 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-bluePrimary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  loading={loginUser.isPending}
                  disabled={loginUser.isPending}
                >
                  {loginUser.isPending ? "Logging in..." : "Log In"}
                </Button>
              </div>
            </form>
            <p className="mt-10 text-left text-sm text-black">
              Don't have an account?{" "}
              <a
                href="/register"
                className="font-semibold leading-6 text-indigo-600 hover:text-indigo-500"
              >
                Sign Up
              </a>
            </p>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default Login;
